import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Radio,
    FormControlLabel,
    RadioGroup,
    Divider,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Warning as WarningIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { ApiDetails } from '../../../../../Constants';
import { handle401Unauthorized, handle403Forbidden } from '../../../../../utils/authErrorHandler';
import SignaturePad from './SignaturePad';
import { validateSignatureData } from '../services/agreementSignatureApi';

const SignatureSelector = ({
    agreementId,
    token,
    onSignatureSelected,
    onCancel,
    agreementTitle = "Agreement",
    allowMultipleSelection = false,
    selectedAgreementIds = [agreementId]
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // State management
    const [stage, setStage] = useState('loading');
    const [signatures, setSignatures] = useState([]);
    const [selectedSignatureId, setSelectedSignatureId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [signatureToDelete, setSignatureToDelete] = useState(null);
    const [newSignatureData, setNewSignatureData] = useState(null);
    const [showNewSignatureConfirm, setShowNewSignatureConfirm] = useState(false);

    // Fetch signatures on mount
    useEffect(() => {
        fetchSignatures();
    }, [token]);

    const fetchSignatures = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/landlord/signature', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'token': token
                },
                body: JSON.stringify({})
            });

            if (response.status === 401) {
                handle401Unauthorized();
                setError('Authentication required. Please log in again.');
                setStage('error');
                return;
            }

            if (response.status === 403) {
                handle403Forbidden();
                setError('You do not have permission to access your signatures.');
                setStage('error');
                return;
            }

            const data = await response.json();

            if (response.status === 200) {
                let signatureList = [];

                // Backend returns either:
                // 1. Single signature object with has_signature: true
                // 2. Array of signatures in data.signatures
                // 3. Empty response if no signatures

                if (data.has_signature && data.signature_id) {
                    // Convert single signature object to array format
                    console.log('Single signature found, converting to array');
                    console.log('Signature data type:', typeof data.signature_data);
                    console.log('Signature data length:', data.signature_data ? data.signature_data.length : 'null');
                    console.log('Signature data starts with:', data.signature_data ? data.signature_data.substring(0, 50) : 'null');

                    signatureList = [{
                        signature_id: data.signature_id,
                        signature_data: data.signature_data,
                        created_at: data.created_at,
                        updated_at: data.updated_at,
                        is_active: true
                    }];
                } else if (Array.isArray(data.signatures)) {
                    // Handle array format if backend returns multiple signatures
                    signatureList = data.signatures;
                }

                console.log('Signatures loaded:', signatureList.length);
                if (signatureList.length > 0) {
                    console.log('First signature data:', signatureList[0]);
                }

                if (signatureList.length === 0) {
                    setStage('drawNew');
                } else {
                    setSignatures(signatureList);
                    if (signatureList.length > 0) {
                        setSelectedSignatureId(signatureList[0].signature_id);
                    }
                    setStage('selectSignature');
                }
            } else {
                const errorMsg = data.message || data.error || 'Failed to fetch signatures';
                setError(errorMsg);
                setStage('error');
            }
        } catch (err) {
            console.error('Error fetching signatures:', err);
            setError('Network error. Please try again.');
            setStage('error');
        } finally {
            setLoading(false);
        }
    };

    const handleSignatureSelected = async () => {
        if (!selectedSignatureId) {
            setError('Please select a signature');
            return;
        }

        if (!agreementId) {
            console.error('No agreement ID provided');
            setError('Missing agreement ID. Please try again.');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);

            console.log('Signing agreement with stored signature using /landlord/sign-with-stored');

            const signResponse = await fetch(ApiDetails + 'pegasus/visionary/agreements/landlord/sign-with-stored', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'token': token
                },
                body: JSON.stringify({
                    agreement_id: agreementId
                })
            });

            if (signResponse.status === 401) {
                handle401Unauthorized();
                setError('Authentication required. Please log in again.');
                setSubmitting(false);
                return;
            }

            if (signResponse.status === 403) {
                handle403Forbidden();
                setError('You do not have permission to sign this agreement.');
                setSubmitting(false);
                return;
            }

            const signData = await signResponse.json();
            console.log('Sign with stored response status:', signResponse.status);
            console.log('Sign with stored response data:', signData);

            if (signResponse.status === 200 || signResponse.status === 201) {
                console.log('Agreement signed successfully with stored signature');
                if (onSignatureSelected) {
                    const selectedSignature = signatures.find(sig => sig.signature_id === selectedSignatureId);
                    onSignatureSelected({
                        success: true,
                        signatureId: selectedSignatureId,
                        signatureData: selectedSignature?.signature_data,
                        signatureMethod: 'selected',
                        agreements: selectedAgreementIds,
                        signResult: signData
                    });
                }
                setStage('success');
            } else {
                console.error('Failed to sign agreement with stored signature:', signData);
                setError(signData.message || signData.error || 'Failed to sign agreement');
            }
        } catch (err) {
            console.error('Error signing agreement with stored signature:', err);
            setError('Network error. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDrawNewSignature = (signatureData) => {
        console.log('handleDrawNewSignature called with data:', signatureData ? 'received' : 'null');

        if (!validateSignatureData(signatureData)) {
            console.error('Signature validation failed for data:', signatureData);
            setError('Invalid signature format. Please try again.');
            return;
        }

        console.log('Signature validation passed, showing confirmation dialog');
        setNewSignatureData(signatureData);
        setShowNewSignatureConfirm(true);
    };

    const handleConfirmNewSignature = async () => {
        console.log('handleConfirmNewSignature called');
        if (!newSignatureData) {
            console.error('No signature data available');
            return;
        }

        if (!agreementId) {
            console.error('No agreement ID provided');
            setError('Missing agreement ID. Please try again.');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            setShowNewSignatureConfirm(false);

            console.log('Signing agreement with new signature using /landlord/sign endpoint');

            const signResponse = await fetch(ApiDetails + 'pegasus/visionary/agreements/landlord/sign', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'token': token
                },
                body: JSON.stringify({
                    agreement_id: agreementId,
                    signature_data: newSignatureData
                })
            });

            if (signResponse.status === 401) {
                handle401Unauthorized();
                setError('Authentication required. Please log in again.');
                setSubmitting(false);
                return;
            }

            if (signResponse.status === 403) {
                handle403Forbidden();
                setError('You do not have permission to sign this agreement.');
                setSubmitting(false);
                return;
            }

            const signData = await signResponse.json();
            console.log('Sign response status:', signResponse.status);
            console.log('Sign response data:', signData);

            if (signResponse.status === 200 || signResponse.status === 201) {
                console.log('Agreement signed successfully with new signature');
                if (onSignatureSelected) {
                    console.log('Calling onSignatureSelected callback');
                    onSignatureSelected({
                        success: true,
                        signatureData: newSignatureData,
                        signatureMethod: 'new',
                        agreements: selectedAgreementIds,
                        isNewSignature: true,
                        signResult: signData
                    });
                }
                setStage('success');
            } else {
                console.error('Failed to sign agreement:', signData);
                setError(signData.message || signData.error || 'Failed to sign agreement');
                setStage('drawNew');
            }
        } catch (err) {
            console.error('Error signing agreement:', err);
            setError('Network error. Please try again.');
            setStage('drawNew');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteSignature = async (signatureId) => {
        try {
            setSubmitting(true);

            const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/landlord/signature/delete', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'token': token
                },
                body: JSON.stringify({
                    signature_id: signatureId
                })
            });

            if (response.status === 200) {
                setSignatures(prev => prev.filter(sig => sig.signature_id !== signatureId));

                if (selectedSignatureId === signatureId) {
                    if (signatures.length > 1) {
                        setSelectedSignatureId(signatures[0].signature_id === signatureId ? signatures[1].signature_id : signatures[0].signature_id);
                    } else {
                        setStage('drawNew');
                    }
                }

                setShowDeleteConfirm(false);
                setSignatureToDelete(null);
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to delete signature');
            }
        } catch (err) {
            console.error('Error deleting signature:', err);
            setError('Network error. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Unknown date';
        try {
            return new Date(timestamp).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (err) {
            return 'Invalid date';
        }
    };

    // Loading state
    if (loading && stage === 'loading') {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                gap: 2
            }}>
                <CircularProgress />
                <Typography color="textSecondary">
                    Loading your signatures...
                </Typography>
            </Box>
        );
    }

    // Error state
    if (error && stage === 'error') {
        return (
            <Paper sx={{ p: 3, bgcolor: '#fff3cd', borderLeft: '4px solid #ffc107' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <WarningIcon sx={{ color: '#ff6f00' }} />
                    <Typography variant="h6" sx={{ color: '#ff6f00', m: 0 }}>
                        Unable to Load Signatures
                    </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {error}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={fetchSignatures}
                        variant="contained"
                        size="small"
                        sx={{ bgcolor: '#ff6f00' }}
                    >
                        <RefreshIcon sx={{ mr: 1, fontSize: 18 }} />
                        Retry
                    </Button>
                    <Button
                        onClick={() => setStage('drawNew')}
                        variant="outlined"
                        size="small"
                    >
                        Draw New Signature
                    </Button>
                </Box>
            </Paper>
        );
    }

    // Success state
    if (stage === 'success') {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                gap: 2,
                textAlign: 'center'
            }}>
                <CheckCircleIcon sx={{ fontSize: 64, color: '#4caf50' }} />
                <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 600 }}>
                    Ready to Sign!
                </Typography>
                <Typography color="textSecondary">
                    Your signature has been selected. Click the sign button to proceed.
                </Typography>
            </Box>
        );
    }

    // Draw new signature stage
    if (stage === 'drawNew') {
        return (
            <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'center' }}>
                        {signatures.length > 0 ? 'Draw a new signature' : 'No signatures found. Please draw your first signature'}
                    </Typography>

                    <SignaturePad
                        onSave={handleDrawNewSignature}
                        onCancel={() => {
                            if (signatures.length > 0) {
                                setStage('selectSignature');
                            } else {
                                onCancel && onCancel();
                            }
                        }}
                        signerName="Landlord"
                        loading={submitting}
                    />

                    {signatures.length > 0 && (
                        <Button
                            onClick={() => setStage('selectSignature')}
                            disabled={submitting}
                            variant="text"
                            sx={{ textTransform: 'none', color: '#666' }}
                        >
                            ← Back to Your Signatures
                        </Button>
                    )}
                </Box>

                {/* CONFIRMATION DIALOG INSIDE DRAWNEW STAGE */}
                <Dialog
                    open={showNewSignatureConfirm}
                    onClose={() => !submitting && setShowNewSignatureConfirm(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Save New Signature?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            This signature will be saved and available for all future agreements.
                        </Typography>

                        {newSignatureData && (
                            <Box sx={{
                                p: 2,
                                bgcolor: '#f5f5f5',
                                borderRadius: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '100px'
                            }}>
                                <img
                                    src={newSignatureData}
                                    alt="New signature"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100px',
                                        objectFit: 'contain'
                                    }}
                                />
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setShowNewSignatureConfirm(false);
                                setStage('drawNew');
                                setNewSignatureData(null);
                            }}
                            disabled={submitting}
                        >
                            Back to Drawing
                        </Button>
                        <Button
                            onClick={handleConfirmNewSignature}
                            disabled={submitting}
                            variant="contained"
                            sx={{ bgcolor: '#4caf50' }}
                        >
                            {submitting ? 'Saving...' : 'Confirm & Save'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    // Select signature stage
    if (stage === 'selectSignature' && signatures.length > 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {error && (
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Paper sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                        📋 Select a signature to use for signing {selectedAgreementIds.length > 1 ? `${selectedAgreementIds.length} agreements` : 'this agreement'}
                    </Typography>
                </Paper>

                <RadioGroup
                    value={selectedSignatureId || ''}
                    onChange={(e) => setSelectedSignatureId(Number(e.target.value))}
                >
                    <Grid container spacing={2}>
                        {signatures.map((signature) => (
                            <Grid item xs={12} sm={6} md={4} key={signature.signature_id}>
                                <FormControlLabel
                                    value={signature.signature_id}
                                    control={<Radio />}
                                    label={
                                        <Card
                                            sx={{
                                                width: '100%',
                                                cursor: 'pointer',
                                                border: selectedSignatureId === signature.signature_id
                                                    ? '2px solid #398DD2'
                                                    : '1px solid #ddd',
                                                transition: 'all 0.3s',
                                                '&:hover': {
                                                    boxShadow: 3,
                                                    borderColor: '#398DD2'
                                                },
                                                ml: 1
                                            }}
                                        >
                                        <Box
                                            sx={{
                                                height: 120,
                                                bgcolor: '#f5f5f5',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                p: 1,
                                                borderBottom: '1px solid #eee'
                                            }}
                                        >
                                            {signature.signature_data ? (
                                                <img
                                                    src={signature.signature_data}
                                                    alt="Signature"
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '100%',
                                                        width: 'auto',
                                                        height: 'auto',
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                            ) : (
                                                <Typography color="textSecondary">
                                                    No preview
                                                </Typography>
                                            )}
                                        </Box>

                                        <CardContent sx={{ p: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>
                                                    Signature #{signature.signature_id}
                                                </Typography>
                                                {signature.is_active && (
                                                    <Chip
                                                        label="Active"
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>

                                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                                                Saved: {formatDate(signature.created_at)}
                                            </Typography>

                                            {signature.used_count && (
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                                                    Used: {signature.used_count} time{signature.used_count !== 1 ? 's' : ''}
                                                </Typography>
                                            )}

                                            <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                                                    disabled={submitting}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSignatureToDelete(signature);
                                                        setShowDeleteConfirm(true);
                                                    }}
                                                    sx={{ flex: 1, fontSize: '0.7rem' }}
                                                >
                                                    Delete
                                                </Button>
                                            </Box>
                                        </CardContent>
                                        </Card>
                                    }
                                    sx={{ m: 0, width: '100%' }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </RadioGroup>

                <Divider sx={{ my: 2 }} />

                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'flex-end'
                }}>
                    <Button
                        onClick={() => setStage('drawNew')}
                        disabled={submitting}
                        variant="outlined"
                        fullWidth={isMobile}
                        sx={{
                            textTransform: 'none',
                            fontSize: '14px',
                            borderColor: '#398DD2',
                            color: '#398DD2',
                            '&:hover': {
                                bgcolor: 'rgba(57, 141, 210, 0.08)'
                            }
                        }}
                    >
                        <AddIcon sx={{ mr: 1, fontSize: 18 }} />
                        Draw New Signature
                    </Button>

                    <Button
                        onClick={handleSignatureSelected}
                        disabled={!selectedSignatureId || submitting}
                        variant="contained"
                        fullWidth={isMobile}
                        sx={{
                            bgcolor: '#4caf50',
                            textTransform: 'none',
                            fontSize: '14px',
                            fontWeight: 500,
                            '&:hover': {
                                bgcolor: '#45a049'
                            }
                        }}
                    >
                        {submitting ? (
                            <>
                                <CircularProgress size={18} sx={{ mr: 1 }} />
                                Preparing...
                            </>
                        ) : (
                            <>
                                <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} />
                                Use Selected Signature
                            </>
                        )}
                    </Button>

                    {onCancel && (
                        <Button
                            onClick={onCancel}
                            disabled={submitting}
                            variant="text"
                            fullWidth={isMobile}
                            sx={{ textTransform: 'none', fontSize: '14px', color: '#666' }}
                        >
                            Cancel
                        </Button>
                    )}
                </Box>

                <Dialog
                    open={showDeleteConfirm}
                    onClose={() => !submitting && setShowDeleteConfirm(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Delete Signature?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Are you sure you want to delete this signature? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setShowDeleteConfirm(false)}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (signatureToDelete) {
                                    handleDeleteSignature(signatureToDelete.signature_id);
                                }
                            }}
                            disabled={submitting}
                            color="error"
                            variant="contained"
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    return null;
};

export default SignatureSelector;
